import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import ReactDOM from 'react-dom';
import { Autocomplete, CircularProgress, createTheme, TextField, ThemeProvider } from '@mui/material';

import FormButton from '../Forms/FormButton/FormButton';
import Fader from '../Loaders/Fader';

import { TABLE_THEME } from '../../../util/constants/AppConstants';
import { assignTags, getTags } from '../../../http/tagAPI';
import { setNotify } from '../../../datastore/actions/actionActions';
import useOutsideClick from '../../../hooks/useOutsideClick';

import './TagWindow.scss';

const TagWindow = ({ isOpen, onCloseHandler, activeCase, handleGetCases }) => {
  const dispatch = useDispatch();

  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [saving, setSaving] = useState(false);

  const config = useSelector((state) => state.config.config);

  const { userInfo } = useOutletContext();

  const windowRef = useRef();

  const theme = createTheme(TABLE_THEME);

  const handleGetAllTags = async () => {
    setLoading(true);

    const res = await getTags(config, userInfo.id);

    if (res.status === 200) {
      setTags(res.data.tags);
      setLoading(false);
    } else {
      console.log('ERROR: Grabbing Tags');
    }
  };

  useEffect(() => {
    if (config.api && userInfo) {
      handleGetAllTags();
    }
  }, [config, userInfo]);

  useEffect(() => {
    if (activeCase) {
      setSelectedTags(activeCase.tags);
    }
  }, [activeCase]);

  const handleAssignTags = async () => {
    setSaving(true);

    const res = await assignTags(config, userInfo.id, activeCase.case_id, selectedTags);

    if (res.status === 200) {
      dispatch(
        setNotify({
          status: true,
          type: 'success',
          title: 'Success',
          message: res.data.message,
        }),
      );

      handleGetCases();
      onCloseHandler();
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: res.data.message,
        }),
      );
    }
    setSaving(false);
  };

  useOutsideClick(windowRef, null, isOpen, onCloseHandler);

  return ReactDOM.createPortal(
    <ThemeProvider theme={theme}>
      <div className="login-wrap feedback">
        <div className={`login-view ${saving ? 'saving' : ''}`}>
          <div ref={windowRef} className="login-box tags__box">
            {!saving ? (
              <>
                <Autocomplete
                  multiple
                  freeSolo
                  disablePortal
                  open={true}
                  options={tags}
                  value={selectedTags}
                  loading={loading}
                  loadingText="Loading tags…"
                  noOptionsText="No tags found"
                  filterSelectedOptions
                  onChange={(event, newValue) => {
                    const uniqueTags = Array.from(new Map(newValue.map((tag) => [tag.toLowerCase(), tag])).values());

                    setSelectedTags(uniqueTags);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add new tags or select from existing"
                      placeholder="Type tag name (Enter to add)"
                      autoFocus
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <div className="submit">
                  <FormButton
                    subLink={'Cancel'}
                    subLinkClick={onCloseHandler}
                    text={'Submit'}
                    onClick={handleAssignTags}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="info__title">Saving Tags</div>
                <div className="info__text">The process should take no longer than 30 seconds. Please Wait...</div>
                <Fader size={50} />
              </>
            )}
          </div>
        </div>
      </div>
    </ThemeProvider>,
    document.getElementById('root'),
  );
};

export default TagWindow;
